import { useMemo, useState } from 'react';
import { Table, SortTH } from 'foundations-components/transitional/components/Table';
import { EmptyState } from 'foundations-components/transitional/components/EmptyState';
import { Illustration } from 'foundations-components/transitional/components/Illustration';
import { Flex } from 'foundations-components/components/Flex';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Text } from 'foundations-components/components/Text';
// @ts-expect-error no types
import localeComparator from 'I18n/utils/localeComparator';
import formatName from 'I18n/utils/formatName';
import { InstalledByRow } from './InstalledByRow';
import { UninstallModal } from '../UninstallModal';
import { useFetchUsersWithInstallAccess } from '../../../hooks/queries/useFetchUsersWithInstallAccess';
import { useFetchModalPermissions } from '../../../hooks/queries/useFetchModalPermissions';
import { AccessTypeTag } from '../AccessTypeTag';
import { View } from 'foundations-components/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const TableEmptyStateDescription = ({
  appAccessType
}) => {
  switch (appAccessType) {
    case 'PERSONAL':
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "app-governance-lib.installedBy.table.emptyState.description.personal"
      });
    // We don't want to have no description if a new access type is introduced - so we can add a default case here
    case 'SHARED':
    default:
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "app-governance-lib.installedBy.table.emptyState.description.shared"
      });
  }
};
export function InstalledByTableEmptyState() {
  const {
    data: {
      appAccessType
    }
  } = useFetchModalPermissions();
  return /*#__PURE__*/_jsx(EmptyState, {
    marginBlockStart: "600",
    titleText: /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      gap: "400",
      alignItems: "start",
      children: [/*#__PURE__*/_jsx(Text, {
        typography: "heading-300",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "app-governance-lib.installedBy.table.emptyState.title"
        })
      }), /*#__PURE__*/_jsx(AccessTypeTag, {
        accessType: appAccessType
      })]
    }),
    primaryContent: /*#__PURE__*/_jsx(View, {
      marginBlockStart: "200",
      children: /*#__PURE__*/_jsx(TableEmptyStateDescription, {
        appAccessType: appAccessType
      })
    }),
    secondaryContent: /*#__PURE__*/_jsx(Illustration, {
      name: "team-empty-state"
    }),
    secondaryContentWidth: 150
  });
}
const SORT_FIELDS = {
  NAME: 'name',
  INSTALL_DATE: 'installDate'
};
export function InstalledByTable({
  users
}) {
  const [uninstallUser, setUninstallUser] = useState(null);
  const {
    superAdmins,
    integrationsManagementWriteUsers
  } = useFetchUsersWithInstallAccess();
  const {
    isSuperAdmin,
    isIntegrationsManagementWriteUser
  } = useMemo(() => {
    if (!uninstallUser || !superAdmins || !integrationsManagementWriteUsers) {
      return {
        isSuperAdmin: false,
        isIntegrationsManagementWriteUser: false
      };
    }
    return {
      isSuperAdmin: !!superAdmins[uninstallUser === null || uninstallUser === void 0 ? void 0 : uninstallUser.userId],
      isIntegrationsManagementWriteUser: !!integrationsManagementWriteUsers[uninstallUser === null || uninstallUser === void 0 ? void 0 : uninstallUser.userId]
    };
  }, [superAdmins, integrationsManagementWriteUsers, uninstallUser]);
  const [sortState, setSortState] = useState({
    field: SORT_FIELDS.NAME,
    direction: 'ascending'
  });
  const getSortDirection = field => {
    return sortState.field === field ? sortState.direction : 'none';
  };
  const sortedUsers = useMemo(() => {
    const sorted = [...users].sort((a, b) => {
      let comparison = 0;
      switch (sortState.field) {
        case SORT_FIELDS.NAME:
          {
            const nameA = formatName({
              firstName: a.firstName,
              lastName: a.lastName,
              email: a.email
            }).toLowerCase();
            const nameB = formatName({
              firstName: b.firstName,
              lastName: b.lastName,
              email: b.email
            }).toLowerCase();
            comparison = localeComparator(nameA, nameB);
            break;
          }
        case SORT_FIELDS.INSTALL_DATE:
          {
            comparison = a.installedAt - b.installedAt;
            break;
          }
        default:
          {
            comparison = 0;
            break;
          }
      }
      return sortState.direction === 'descending' ? -comparison : comparison;
    });
    return sorted;
  }, [users, sortState]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [uninstallUser && /*#__PURE__*/_jsx(UninstallModal, {
      user: uninstallUser,
      onClose: () => setUninstallUser(null),
      isSuperAdmin: isSuperAdmin,
      isIntegrationsManagementWriteUser: isIntegrationsManagementWriteUser
    }), /*#__PURE__*/_jsxs(Table, {
      bordered: true,
      children: [/*#__PURE__*/_jsx("thead", {
        children: /*#__PURE__*/_jsxs("tr", {
          children: [/*#__PURE__*/_jsx(SortTH, {
            sort: getSortDirection(SORT_FIELDS.NAME),
            onSortChange: ({
              target: {
                value
              }
            }) => {
              setSortState({
                field: SORT_FIELDS.NAME,
                direction: value
              });
            },
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "app-governance-lib.installedBy.table.header.name"
            })
          }), /*#__PURE__*/_jsx(SortTH, {
            sort: getSortDirection(SORT_FIELDS.INSTALL_DATE),
            onSortChange: ({
              target: {
                value
              }
            }) => {
              setSortState({
                field: SORT_FIELDS.INSTALL_DATE,
                direction: value
              });
            },
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "app-governance-lib.installedBy.table.header.installDate"
            })
          }), /*#__PURE__*/_jsx("th", {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "app-governance-lib.installedBy.table.header.actions"
            })
          })]
        })
      }), /*#__PURE__*/_jsx("tbody", {
        children: sortedUsers.map(user => /*#__PURE__*/_jsx(InstalledByRow, {
          installedInfo: user,
          onUninstallClick: () => setUninstallUser(user)
        }, user.userId))
      })]
    })]
  });
}